// Dependencias
import Swal from 'sweetalert2';
import { AxiosError } from 'axios';

// Configuraciones
import { alertConfigurations } from '../config/sweetAlert.config';

// Services
import { ApiKey, ApiKeyStatus, revokeApiKey } from '../service/publicApi.service';

/**
 * Escapa texto para insertarlo de forma segura en HTML (datos controlados por usuarios finales:
 * nombres de key, motivos, nombres de usuario, rutas, etc.)
 */
export function escapeHtml(value: unknown): string {
  if (value === null || value === undefined) return '';
  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

const dateTimeFormatter = new Intl.DateTimeFormat('es-MX', {
  dateStyle: 'short',
  timeStyle: 'medium'
});

/**
 * Formatea una fecha (ISO) a fecha y hora local del navegador
 */
export function formatDateTime(date: string | null | undefined, empty = '<span class="text-white-50">-</span>') {
  if (!date) return empty;
  const parsed = new Date(date);
  if (Number.isNaN(parsed.getTime())) return empty;
  return escapeHtml(dateTimeFormatter.format(parsed));
}

/**
 * Badge del estado de la key
 */
export function drawApiKeyStatus(status: ApiKeyStatus) {
  if (status === 'active') return '<span class="badge bg-success">Activa</span>';
  if (status === 'revoked') return '<span class="badge bg-danger">Revocada</span>';
  if (status === 'expired') return '<span class="badge bg-warning text-dark">Expirada</span>';
  return `<span class="badge bg-secondary">${escapeHtml(status)}</span>`;
}

/**
 * Badge del ambiente de la key
 */
export function drawApiKeyEnvironment(environment: string) {
  return environment === 'live'
    ? '<span class="badge bg-primary">live</span>'
    : `<span class="badge bg-secondary">${escapeHtml(environment)}</span>`;
}

/**
 * Lista de scopes como badges
 */
export function drawApiKeyScopes(scopes: string | null) {
  if (!scopes) return '<span class="text-white-50">-</span>';
  return scopes
    .split(',')
    .map(scope => scope.trim())
    .filter(Boolean)
    .map(scope => `<span class="badge bg-dark border border-secondary me-1 mb-1">${escapeHtml(scope)}</span>`)
    .join('');
}

/**
 * Celda del usuario dueño de la key (nombre + correo + id) con enlace a su detalle
 */
export function drawApiKeyUser(key: ApiKey) {
  const fullName = [key.user_name, key.user_last_name].filter(Boolean).join(' ') || 'Sin nombre';
  const apiBadge = key.user_api
    ? '<span class="badge bg-success ms-1" title="Permiso users.api habilitado">api=1</span>'
    : '<span class="badge bg-danger ms-1" title="Permiso users.api deshabilitado: el backend rechaza sus requests">api=0</span>';
  const blockedBadge = key.user_blocked ? '<span class="badge bg-danger ms-1">Bloqueado</span>' : '';

  return `
    <a class="link-light fw-bold" href="/public-api/users/${Number(key.user_id)}">${escapeHtml(fullName)}</a>${apiBadge}${blockedBadge}
    <div class="small text-white-50">${escapeHtml(key.user_email || '')}</div>
    <div class="small text-white-50">ID: ${Number(key.user_id)}</div>
  `;
}

/**
 * Último uso (fecha + IP)
 */
export function drawApiKeyLastUse(key: ApiKey) {
  if (!key.last_used_at) return '<span class="text-white-50">Nunca</span>';
  return `${formatDateTime(key.last_used_at)}<div class="small text-white-50">${escapeHtml(key.last_used_ip || '')}</div>`;
}

/**
 * Información de revocación (quién y por qué)
 */
export function drawApiKeyRevoked(key: ApiKey) {
  if (!key.revoked_at) return '<span class="text-white-50">-</span>';
  return `
    <div class="small">${formatDateTime(key.revoked_at)}</div>
    <div class="small"><span class="badge bg-secondary">${escapeHtml(key.revoked_by || 'N/A')}</span></div>
    <div class="small text-white-50" style="max-width: 220px; white-space: normal;">${escapeHtml(key.revoked_reason || '')}</div>
  `;
}

/**
 * Botones de acciones de una key (ver detalle / revocar)
 */
export function drawApiKeyActions(key: ApiKey, { showDetail = true } = {}) {
  const detail = showDetail
    ? `<a href="/public-api/keys/${Number(key.id)}" class="btn btn-light btn-sm mb-1"><i class="bi bi-eye"></i> Ver</a>`
    : '';
  const revoke = key.status !== 'revoked'
    ? `<button type="button" class="btn btn-danger btn-sm mb-1 btn-revoke-api-key"
        data-key-id="${Number(key.id)}"
        data-key-name="${escapeHtml(key.name)}"
        data-key-prefix="${escapeHtml(key.key_prefix)}">
        <i class="bi bi-x-octagon"></i> Revocar
      </button>`
    : '';
  return `<div class="d-flex flex-column gap-1">${detail}${revoke}</div>`;
}

/**
 * Flujo de revocación: pide motivo (obligatorio), confirma y ejecuta la petición.
 *
 * @returns la key actualizada si se revocó, o null si se canceló
 */
export async function revokeApiKeyFlow({ id, name, prefix }: { id: number; name: string; prefix: string }) {
  // 1. Motivo obligatorio
  const { isConfirmed, value: reason } = await Swal.fire<string>({
    title: 'Revocar API key',
    html: `
      <p>Key <strong>${escapeHtml(name)}</strong> (<code>${escapeHtml(prefix)}</code>)</p>
      <p class="small">La revocación es inmediata: el backend de Signly rechazará cualquier request con esta key.</p>
    `,
    icon: 'warning',
    input: 'textarea',
    inputLabel: 'Motivo de la revocación (obligatorio)',
    inputPlaceholder: 'Ej. Key expuesta públicamente, solicitud del cliente, abuso...',
    inputAttributes: { maxlength: '255' },
    showCancelButton: true,
    confirmButtonText: 'Continuar',
    cancelButtonText: 'Cancelar',
    buttonsStyling: false,
    customClass: {
      confirmButton: 'btn btn-danger mx-2',
      cancelButton: 'btn btn-secondary mx-2'
    },
    inputValidator: (value) => (!value || value.trim().length < 3)
      ? 'El motivo es obligatorio (mínimo 3 caracteres)'
      : undefined
  });
  if (!isConfirmed || !reason) return null;

  // 2. Confirmación final
  const confirmation = await Swal.fire({
    ...alertConfigurations,
    icon: 'question',
    title: '¿Confirmas la revocación?',
    html: `<p>Esta acción no se puede deshacer.</p><p class="small text-muted">Motivo: ${escapeHtml(reason.trim())}</p>`
  });
  if (!confirmation.isConfirmed) return null;

  // 3. Ejecución
  try {
    const { key } = await revokeApiKey(id, reason.trim());
    await Swal.fire({ icon: 'success', title: 'API key revocada', timer: 2000, showConfirmButton: false });
    return key;
  } catch (error) {
    const message = error instanceof AxiosError
      ? (error.response?.data?.message as string) || 'Error al revocar la API key'
      : 'Error al revocar la API key';
    await Swal.fire({ icon: 'error', title: 'No se pudo revocar', text: message });
    return null;
  }
}

/**
 * Ejecuta un callback cuando el DOM esté listo (o de inmediato si ya lo está)
 */
export function onDomReady(callback: () => void) {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', callback);
  } else {
    callback();
  }
}
